import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

// ✅ Change this to your backend base url
const API_BASE = import.meta?.env?.REACT_APP_API_BASEURL || "https://server.path4u.in/api";

const PRIMARY = "#FF8103";

const AdminDashboardStats = () => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [overview, setOverview] = useState(null);

  const [reportType, setReportType] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const token = useMemo(() => {
    // ✅ Change if you store token in another key
    return localStorage.getItem("token");
  }, []);

  const fetchOverview = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API_BASE}/admin/overview`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res?.data?.success) {
        setErr(res?.data?.message || "Failed to load overview");
        setOverview(null);
      } else {
        setOverview(res.data.data);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Something went wrong");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openReport = async (type) => {
    try {
      const res = await axios.get(`${API_BASE}/admin/report/${type}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      setReportType(type);
      setReportData(res.data.data || []);
      setShowReportModal(true);
    } catch (err) {
      alert("Failed to load report");
    }
  };

  const downloadCSV = (data, filename) => {
    if (!data.length) return;

    const headers = Object.keys(data[0]);

    const csv = [
      headers.join(","),
      ...data.map((row) => headers.map((h) => row[h]).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename + ".csv";
    a.click();
  };

  const company = overview?.company;

  const users = overview?.users || {};
  const masters = overview?.masters || {};
  const content = overview?.content || {};
  const services = overview?.services || {};
  const cms = overview?.cms || {};
  const purchases = overview?.purchases || {};
  const revenue = overview?.revenue || {};
  const recent = overview?.recent || {};

  if (loading) return <PageLoader />;
  if (err) return <ErrorState err={err} onRetry={fetchOverview} />;
  if (!overview) return <ErrorState err="No data found." onRetry={fetchOverview} />;

  return (
    <div style={styles.page}>
      {/* Top Bar */}
      <div style={styles.topRow}>
        <div>
          <h1 style={styles.h1}>Admin Dashboard</h1>
          <p style={styles.sub}>
            Overview of platform activity and performance
          </p>
        </div>

        <button onClick={fetchOverview} style={styles.refreshBtn}>
          Refresh
        </button>
      </div>

      {/* Company */}
      <div style={styles.companyCard}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <div style={styles.companyBadge}>🏢</div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16, color: "#111827" }}>
              {company?.name || "Company"}
            </div>
            <div style={{ color: "#6B7280", fontWeight: 700, fontSize: 13 }}>
              {company?.address || "—"}
            </div>
          </div>
        </div>

        <div style={styles.companyMeta}>
          <MetaChip label="Phone" value={company?.phone} />
          <MetaChip label="Email" value={company?.email} />
          <MetaChip label="Website" value={company?.website} />
          <MetaChip label="GSTIN" value={company?.gstin} />
        </div>
      </div>

      <div style={styles.grid4}>
        <button style={styles.reportBtn} onClick={() => openReport("students")}>
          All Students
        </button>

        <button style={styles.reportBtn} onClick={() => openReport("homeTutor")}>
          Home Tutor
        </button>

        <button style={styles.reportBtn} onClick={() => openReport("homeWork")}>
          Home Work Assistance
        </button>

        <button style={styles.reportBtn} onClick={() => openReport("subjective")}>
          Subjective Exams
        </button>

        <button style={styles.reportBtn} onClick={() => openReport("payments")}>
          Payment
        </button>

        <button style={styles.reportBtn} onClick={() => openReport("notes")}>
          Exam Notes
        </button>

        <button style={styles.reportBtn} onClick={() => openReport("subadmins")}>
          Sub Admin
        </button>

        <button style={styles.reportBtn} onClick={() => openReport("appointments")}>
          Appointment
        </button>
      </div>

      {/* KPI Cards */}
      <SectionTitle title="Key Metrics" />
      <div style={styles.grid4}>
        <KpiCard title="Students" value={users?.students?.total} sub={`Verified: ${users?.students?.verified ?? 0}`} />
        <KpiCard title="Teachers" value={users?.teachers?.total} sub={`Pending: ${users?.teachers?.pendingApproval ?? 0}`} />
        <KpiCard title="Notes" value={content?.notes} sub={`Subjective: ${content?.subjectiveExams ?? 0}`} />
        <KpiCard title="Purchases" value={sumPurchases(purchases)} sub="Total subscriptions" />
      </div>

      {/* Services */}
      <SectionTitle title="Services" />
      <div style={styles.grid3}>
        <StatCard
          title="Home Tutor"
          rows={[
            ["Total", services?.homeTutorRequests?.total],
            ["Pending", services?.homeTutorRequests?.pending],
          ]}
        />
        <StatCard
          title="Homework Assistance"
          rows={[
            ["Total", services?.homeWorkAssistance?.total],
            ["Pending", services?.homeWorkAssistance?.pending],
          ]}
        />
        <StatCard
          title="Appointments"
          rows={[
            ["Total", services?.appointments?.total],
            ["Pending", services?.appointments?.pending],
          ]}
        />
      </div>

      {/* Content */}
      <SectionTitle title="Content Moderation" />
      <div style={styles.grid3}>
        <StatCard
          title="Checked Exams"
          rows={[
            ["Total", content?.checkedExams?.total],
            ["Pending", content?.checkedExams?.pending],
            ["Approved", content?.checkedExams?.approved],
            ["Rejected", content?.checkedExams?.rejected],
          ]}
        />
        <StatCard
          title="Notes"
          rows={[
            ["Total", content?.notes],
            ["Subjective Exams", content?.subjectiveExams],
            ["Submitted Exams", content?.submittedExams],
          ]}
        />
        <StatCard
          title="Live Classes"
          rows={[
            ["Total", services?.liveClasses?.total],
            ["Purchases", services?.liveClasses?.purchases],
          ]}
        />
      </div>

      {/* Masters + CMS */}
      <SectionTitle title="Masters & CMS" />
      <div style={styles.grid2}>
        <StatCard
          title="Master Data"
          rows={[
            ["States", masters?.states],
            ["Cities", masters?.cities],
            ["Boards", masters?.boards],
            ["Classes", masters?.classes],
            ["Subjects", masters?.subjects],
            ["Competitions", masters?.competitions],
            ["Universities", masters?.universities],
            ["Courses", masters?.courses],
            ["Semesters", masters?.semesters],
            ["College Subjects", masters?.collegeSubjects],
            ["NIOS Classes", masters?.niosClasses],
            ["NIOS Subjects", masters?.niosSubjects],
          ]}
        />
        <StatCard
          title="CMS"
          rows={[
            ["Sliders", cms?.sliders],
            ["Notifications", cms?.notifications],
            ["Coupons (Total)", cms?.coupons?.total],
            ["Coupons (Active)", cms?.coupons?.active],
            ["Councils", cms?.councils],
            ["Jobs (Total)", cms?.jobs?.total],
            ["Jobs (Active)", cms?.jobs?.active],
            ["Scholarships", cms?.scholarships],
            ["Career Categories", cms?.chooseCareer?.categories],
            ["Blogs", cms?.chooseCareer?.blogs],
          ]}
        />
      </div>

      {/* Revenue */}
      <SectionTitle title="Revenue" />
      <div style={styles.grid3}>
        <KpiCard title="Invoices Total" value={formatMoney(revenue?.invoicesTotal)} sub="All time" />
        <KpiCard title="Invoices Last 30 Days" value={formatMoney(revenue?.invoicesLast30Days)} sub="Last 30 days" />
        <KpiCard title="Live Class Total" value={formatMoney(revenue?.liveClassTotal)} sub="May need numeric price" />
      </div>

      {/* Recent */}
      <SectionTitle title="Recent Activity" />



      <div style={styles.grid2}>
        <RecentTable
          title="Recent Students"
          columns={["Name", "Mobile", "Email", "Created"]}
          rows={(recent?.students || []).map((s) => [
            s.fullName || "—",
            s.mobile || "—",
            s.email || "—",
            formatDate(s.createdAt),
          ])}
        />
        <RecentTable
          title="Recent Teachers"
          columns={["Mobile", "Approval", "Verified", "Created"]}
          rows={(recent?.teachers || []).map((t) => [
            t.mobile || "—",
            t.approvalStatus || "—",
            String(t.isVerified ?? false),
            formatDate(t.createdAt),
          ])}
        />
      </div>

      <div style={styles.grid2}>
        <RecentTable
          title="Recent Jobs"
          columns={["Job", "Company", "Status", "Apply Till"]}
          rows={(recent?.jobs || []).map((j) => [
            j.jobTitle || "—",
            j.companyName || "—",
            j.status || "—",
            formatDate(j.lastDateToApply),
          ])}
        />
        <RecentTable
          title="Recent Notes"
          columns={["Title", "Lang", "Video", "Created"]}
          rows={(recent?.notes || []).map((n) => [
            n.noteTitle || "—",
            n.language || "—",
            n.videoUrl ? "Yes" : "No",
            formatDate(n.createdAt),
          ])}
        />
      </div>

      <div style={{ height: 18 }} />
      {showReportModal && (
        <div style={styles.modalOverlay}>
          {/* <div style={styles.modal}> */}
          <div
            style={{
              ...styles.modal,
              width: isFullScreen ? "100%" : "700px",
              height: isFullScreen ? "100%" : "auto",
              maxWidth: isFullScreen ? "100%" : "700px"
            }}
          >
            {/* <div style={{ display: "flex", justifyContent: "space-between" }}>
              <h3>{reportType} Report</h3>
              

              <button onClick={() => setShowReportModal(false)}>Close</button>
            </div> */}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>{reportType} Report</h3>

              <div style={{ display: "flex", gap: "10px" }}>
                <button onClick={() => setIsFullScreen(!isFullScreen)}>
                  {isFullScreen ? "⬇" : "⬆"}
                </button>

                <button onClick={() => setShowReportModal(false)}>Close</button>
              </div>
            </div>

            {/* <div style={{ maxHeight: 400, overflow: "auto" }}> */}
            <div style={{ maxHeight: isFullScreen ? "80vh" : 400, overflow: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {reportData[0] &&
                      Object.keys(reportData[0]).map((col) => (
                        <th key={col} style={styles.th}>
                          {col}
                        </th>
                      ))}
                  </tr>
                </thead>

                <tbody>
                  {reportData.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => (
                        <td key={j} style={styles.td}>
                          {String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => downloadCSV(reportData, reportType)}
              style={styles.downloadBtn}
            >
              Download CSV
            </button>
          </div>
        </div>
      )}
    </div>

  );
};

export default AdminDashboardStats;

/* -------------------- Helpers -------------------- */

function sumPurchases(p) {
  if (!p) return 0;
  return (
    (p.assignmentSubscriptions || 0) +
    (p.examNotesPurchases || 0) +
    (p.homeTutorSubscriptions || 0) +
    (p.homeWorkPurchases || 0) +
    (p.subjectiveExamSubscriptions || 0)
  );
}

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "—";
  }
}

function formatMoney(v) {
  const num = Number(v || 0);
  if (Number.isNaN(num)) return "₹0";
  return `₹${num.toLocaleString("en-IN")}`;
}

/* -------------------- UI Pieces -------------------- */

const SectionTitle = ({ title }) => (
  <div style={{ marginTop: 18, marginBottom: 10 }}>
    <div style={styles.sectionTitle}>{title}</div>
  </div>
);

const KpiCard = ({ title, value, sub }) => (
  <div style={styles.kpiCard}>
    <div style={styles.kpiTitle}>{title}</div>
    <div style={styles.kpiValue}>{value ?? 0}</div>
    <div style={styles.kpiSub}>{sub}</div>
  </div>
);

const StatCard = ({ title, rows }) => (
  <div style={styles.card}>
    <div style={styles.cardTitle}>{title}</div>
    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
      {rows.map(([k, v], idx) => (
        <div key={idx} style={styles.row}>
          <div style={styles.rowKey}>{k}</div>
          <div style={styles.rowVal}>{v ?? 0}</div>
        </div>
      ))}
    </div>
  </div>
);

const MetaChip = ({ label, value }) => (
  <div style={styles.metaChip}>
    <span style={{ color: "#6B7280", fontWeight: 900, fontSize: 12 }}>
      {label}:
    </span>
    <span style={{ marginLeft: 6, color: "#111827", fontWeight: 900, fontSize: 12 }}>
      {value || "—"}
    </span>
  </div>
);

const RecentTable = ({ title, columns, rows }) => (
  <div style={styles.card}>
    <div style={styles.cardTitle}>{title}</div>
    <div style={{ overflowX: "auto", marginTop: 10 }}>
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={styles.th}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td style={styles.td} colSpan={columns.length}>No data</td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i}>
                {r.map((cell, j) => (
                  <td key={j} style={styles.td}>{cell}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const PageLoader = () => (
  <div style={styles.center}>
    <div style={styles.loaderBox}>
      <div style={styles.spinner} />
      <div style={{ fontWeight: 900, color: "#111827" }}>Loading overview…</div>
    </div>
  </div>
);

const ErrorState = ({ err, onRetry }) => (
  <div style={styles.center}>
    <div style={styles.errorBox}>
      <div style={{ fontWeight: 900, fontSize: 16, color: "#111827" }}>
        Failed to load
      </div>
      <div style={{ marginTop: 8, color: "#6B7280", fontWeight: 700 }}>
        {err}
      </div>
      <button onClick={onRetry} style={styles.retryBtn}>Retry</button>
    </div>
  </div>
);


// {
//   showReportModal && (
//     <div style={styles.modalOverlay}>
//       <div style={styles.modal}>
//         <div style={{ display: "flex", justifyContent: "space-between" }}>
//           <h3>{reportType} Report</h3>

//           <button onClick={() => setShowReportModal(false)}>Close</button>
//         </div>

//         <div style={{ maxHeight: 400, overflow: "auto" }}>
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 {reportData[0] &&
//                   Object.keys(reportData[0]).map((col) => (
//                     <th key={col} style={styles.th}>
//                       {col}
//                     </th>
//                   ))}
//               </tr>
//             </thead>

//             <tbody>
//               {reportData.map((row, i) => (
//                 <tr key={i}>
//                   {Object.values(row).map((val, j) => (
//                     <td key={j} style={styles.td}>
//                       {String(val)}
//                     </td>
//                   ))}
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         <button
//           onClick={() => downloadCSV(reportData, reportType)}
//           style={styles.downloadBtn}
//         >
//           Download CSV
//         </button>
//       </div>
//     </div>
//   )
// }



/* -------------------- Styles -------------------- */
const styles = {
  page: {
    padding: 24,
    minHeight: "100vh",
    background: "#f8fafc",
    fontFamily: "Inter, sans-serif",
    color: "#0f172a",
  },

  /* HEADER */
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderRadius: 16,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },

  h1: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#0f172a",
  },

  sub: {
    marginTop: 6,
    fontSize: 13,
    color: "#64748b",
  },

  refreshBtn: {
    background: "#0284c7",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer",
    transition: "all 0.25s ease",
  },

  /* COMPANY CARD */
  companyCard: {
    marginTop: 18,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },

  companyBadge: {
    width: 50,
    height: 50,
    borderRadius: 12,
    background: "#e0f2fe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    color: "#0284c7",
  },

  companyMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },

  metaChip: {
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    color: "#475569",
  },

  /* GRID */
  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 16,
  },

  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },

  /* KPI CARD */
  kpiCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
    transition: "all 0.25s ease",
    cursor: "pointer",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },

  kpiTitle: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },

  kpiValue: {
    fontSize: 26,
    fontWeight: 800,
    marginTop: 6,
    color: "#0f172a",
  },

  kpiSub: {
    color: "#0284c7",
    fontSize: 12,
    marginTop: 6,
  },

  /* GENERAL CARD */
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },

  cardTitle: {
    fontSize: 15,
    fontWeight: 800,
    marginBottom: 10,
    color: "#0f172a",
  },

  row: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
  },

  rowKey: {
    color: "#64748b",
    fontSize: 14,
    fontWeight:700
  },

  rowVal: {
    fontWeight: 800,
  },

  /* TABLE */
  table: {
    width: "100%",
    borderCollapse: "collapse",
    borderRadius: 12,
    overflow: "hidden",
  },

  th: {
    textAlign: "left",
    padding: 12,
    fontSize: 12,
    background: "#f1f5f9",
    color: "#64748b",
    fontWeight: 700,
  },

  td: {
    padding: 12,
    fontSize: 14,
        fontWeight: 700,

    borderTop: "1px solid #e2e8f0",
  },

  /* BUTTONS */
  reportBtn: {
    background: "#0284c7",
    border: "none",
    color: "#fff",
    padding: 14,
    borderRadius: 12,
    fontWeight: 800,
    cursor: "pointer",
  },

  downloadBtn: {
    marginTop: 14,
    background: "#22c55e",
    border: "none",
    color: "#fff",
    padding: "10px 18px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer",
  },

  /* MODAL */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.4)",
    backdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },

  modal: {
    background: "#ffffff",
    padding: 20,
    borderRadius: 16,
    width: "90%",
    maxWidth: 1000,
    border: "1px solid #e2e8f0",
    boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
  },

  /* LOADER */
  center: {
    minHeight: "80vh",
    display: "grid",
    placeItems: "center",
  },

  loaderBox: {
    background: "#ffffff",
    padding: 20,
    borderRadius: 16,
    display: "flex",
    gap: 12,
    alignItems: "center",
    border: "1px solid #e2e8f0",
  },

  spinner: {
    width: 20,
    height: 20,
    border: "3px solid #e2e8f0",
    borderTopColor: "#0284c7",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },

  /* ERROR */
  errorBox: {
    background: "#ffffff",
    padding: 20,
    borderRadius: 16,
    maxWidth: 400,
    textAlign: "center",
    border: "1px solid #e2e8f0",
  },

  retryBtn: {
    marginTop: 12,
    background: "#ef4444",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 10,
    cursor: "pointer",
  },
};

/* Add keyframes if you want spinner animation (optional) */
const styleTag = document?.createElement?.("style");
if (styleTag && !document.getElementById("dash-spin-style")) {
  styleTag.id = "dash-spin-style";
  styleTag.innerHTML = `
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @media (max-width: 1100px){
      .grid4 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
      .grid3 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
      .grid2 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
    }
  `;
  document.head.appendChild(styleTag);
}